<script setup lang="ts">
import type { DateValue } from '@internationalized/date'
import type { HTMLAttributes } from 'vue'
import { FaButton, FaDate, FaIcon } from '@fantastic-admin/components'
import { CalendarDate, CalendarDateTime, getLocalTimeZone } from '@internationalized/date'

defineOptions({
  name: 'AppDatePicker',
})

const props = withDefaults(defineProps<{
  modelValue?: DateValue | Date | number | string | null
  type?: 'date' | 'datetime'
  valueFormat?: string
  placeholder?: string
  clearable?: boolean
  disabled?: boolean
  readonly?: boolean
  size?: '' | 'large' | 'default' | 'small'
  class?: HTMLAttributes['class']
  defaultTime?: Date | string
  disabledDate?: (date: Date) => boolean
}>(), {
  modelValue: '',
  type: 'date',
  valueFormat: '',
  placeholder: '选择日期',
  clearable: true,
  disabled: false,
  readonly: false,
  size: 'default',
  class: undefined,
  defaultTime: '00:00:00',
  disabledDate: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'change': [value: string]
  'clear': []
}>()

const dateValue = computed(() => parseModelValue(props.modelValue, props.type === 'datetime'))
const hasValue = computed(() => Boolean(dateValue.value))
const triggerSize = computed(() => props.size === 'small' ? 'sm' : props.size === 'large' ? 'lg' : 'default')
const effectiveValueFormat = computed(() => props.valueFormat || (props.type === 'datetime' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'))
const defaultTime = computed(() => resolveDefaultTime(props.defaultTime))

function parseModelValue(value: typeof props.modelValue, withTime: boolean): DateValue | undefined {
  if (!value) {
    return undefined
  }

  if (typeof value === 'object' && 'year' in value && 'month' in value && 'day' in value) {
    return value as DateValue
  }

  const matched = typeof value === 'string'
    ? value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/)
    : null
  if (matched) {
    const [, year, month, day, hour = '0', minute = '0', second = '0'] = matched
    return withTime
      ? new CalendarDateTime(Number(year), Number(month), Number(day), Number(hour), Number(minute), Number(second))
      : new CalendarDate(Number(year), Number(month), Number(day))
  }

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) {
    return undefined
  }

  if (withTime) {
    return new CalendarDateTime(
      date.getFullYear(),
      date.getMonth() + 1,
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    )
  }

  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

function resolveDefaultTime(value: Date | string) {
  if (value instanceof Date) {
    return [value.getHours(), value.getMinutes(), value.getSeconds()]
      .map(part => String(part).padStart(2, '0'))
      .join(':')
  }
  return /^\d{2}:\d{2}:\d{2}$/.test(value) ? value : '00:00:00'
}

function formatDateValue(value: DateValue) {
  const parts: Record<string, string> = {
    YYYY: String(value.year).padStart(4, '0'),
    MM: String(value.month).padStart(2, '0'),
    DD: String(value.day).padStart(2, '0'),
    HH: String('hour' in value ? value.hour : 0).padStart(2, '0'),
    mm: String('minute' in value ? value.minute : 0).padStart(2, '0'),
    ss: String('second' in value ? value.second : 0).padStart(2, '0'),
  }

  return effectiveValueFormat.value.replace(/YYYY|MM|DD|HH|mm|ss/g, token => parts[token] ?? token)
}

function handleUpdate(value: DateValue | DateValue[] | undefined) {
  const nextValue = Array.isArray(value) ? value[0] : value
  const formattedValue = nextValue ? formatDateValue(nextValue) : ''
  emit('update:modelValue', formattedValue)
  emit('change', formattedValue)
}

function handleClear() {
  if (props.disabled || props.readonly) {
    return
  }
  emit('update:modelValue', '')
  emit('clear')
}

function isDateDisabled(value: DateValue) {
  return props.disabledDate?.(value.toDate(getLocalTimeZone())) ?? false
}
</script>

<template>
  <FaDate
    :model-value="dateValue"
    picker
    :with-time="props.type === 'datetime'"
    :default-time="defaultTime"
    :picker-placeholder="props.placeholder"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :is-date-disabled="props.disabledDate ? isDateDisabled : undefined"
    :format-value="value => value && !Array.isArray(value) ? formatDateValue(value) : props.placeholder"
    @update:model-value="handleUpdate"
  >
    <template #trigger="{ label }">
      <FaButton
        variant="outline"
        :size="triggerSize"
        :disabled="props.disabled"
        class="font-normal text-left w-full justify-start overflow-hidden" :class="[!hasValue && 'text-muted-foreground', props.class]"
      >
        <FaIcon name="i-lucide:calendar" class="flex-none size-4" />
        <span class="flex-1 min-w-0 truncate">{{ label }}</span>
        <span
          v-if="props.clearable && hasValue && !props.readonly"
          class="text-muted-foreground ml-auto rounded-sm inline-flex flex-none size-5 items-center justify-center hover:text-foreground"
          role="button"
          tabindex="0"
          aria-label="清空日期"
          @pointerdown.stop
          @click.stop.prevent="handleClear"
        >
          <FaIcon name="i-lucide:x" class="size-3.5" />
        </span>
      </FaButton>
    </template>
  </FaDate>
</template>
